import styled from "styled-components";
import colors from "core/resources/theme/colors";
import { Empty, Skeleton } from "antd";
import { PaginateResult } from "core/resources/types/shared";
import Pagination from "../Pagination";
import { TableComponent } from "./TableComponent";
import { Box, Flex } from "@chakra-ui/layout";
import { Column } from "react-table";
import { Menu, MenuButton, MenuItem, MenuList } from "@chakra-ui/menu";

import { Button } from "@chakra-ui/button";
import { GoKebabVertical } from "react-icons/go";
import useSelectableColumn from "./useSelectableColumn";


interface StyleProps {
  styles?: any
}

const Styles = styled.div<StyleProps>`
  background-color: white;
  box-shadow:  ${props => props.styles?.boxShadow ? props.styles.boxShadow : '0 0 16px 3px rgba(0, 0, 0, 0.05)'};
  border-radius: 10px;
  overflow: hidden;

  tbody > tr > td {
    border-bottom: 0;
  }

  .rs-tag {
    border-radius: 10px;
    padding-left: 12px;
    padding-right: 12px;
    line-height: 25px;
    border-radius: 15px;

    &.rs-tag-default {
      background-color: ${colors.black100};
    }

    .rs-badge {
      font-size: 10px !important;
    }
  }
`;

const HeaderStyle = styled.div<StyleProps>`
  padding: 16px;
  display: flex;
  
`;

function Table<T = any>({
  styles,
  columns,
  data,
  header,
  loading,
  onChangePage,
  onClickRow,
  actions,
  enableSelected,
  onSelected,
  defaultSelected,
}: {
  styles?: object | any,
  columns: Column[];
  data: PaginateResult<T>;
  header?: any;
  loading?: boolean;
  onChangePage?: Function;
  onClickRow?: (item: T) => void;
  actions?: { title: string; onClick: (item: T) => any }[];
  enableSelected?: boolean;
  onSelected?: (items: T[]) => void;
  defaultSelected?: T[];
}) {
  const { column: selectableColumn } = useSelectableColumn<any>(
    data?.items,
    onSelected,
    defaultSelected
  );

  let _columns: Column[] = [];

  if (actions?.length) {
    _columns = [
      enableSelected ? selectableColumn : undefined,
      ...columns,
      {
        Header: "",
        accessor: "menu",
        width: 80,
        maxWidth: 80,
        minWidth: 80,
        disableClick: true,
        Cell: ({ row: { original } }: { row: { original: T } }) => {
          return (
            <Flex justifyContent="flex-end">
              <Menu>
                <MenuButton type="button">
                  <Button
                    variant="outline"
                    rounded="full"
                    w="40px"
                    h="40px"
                    type="button"
                  >
                    <GoKebabVertical />
                  </Button>
                </MenuButton>
                <MenuList>
                  {actions.map((action, index) => (
                    <MenuItem
                      key={index}
                      fontSize="14px"
                      onClick={() => action.onClick(original)}
                      type="button"
                    >
                      {action.title}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            </Flex>
          );
        },
      } as any,
    ].filter((item: any) => item);
  } else {
    _columns = (
      [enableSelected ? selectableColumn : undefined, ...columns] as any
    ).filter((item: any) => item);
  }

  return (
    <Styles styles={styles}>
      {typeof header === "function" ? (
        <HeaderStyle styles={styles}>{header()}</HeaderStyle>
      ) : null}

      {loading ? (
        <Box p="16px">
          <Skeleton active />
        </Box>
      ) : null}
      {!loading ? (
        <Box p={ styles?.padding ? styles?.padding : "0 0 16px 16px"}>
          <TableComponent
            styles={styles}
            onClickRow={(index: number) => {
              if (typeof onClickRow === "function") {
                onClickRow(data.items[index]);
              }
            }}
            columns={_columns}
            data={data?.items ?? []}
          />
        </Box>
      ) : null}

      {!loading && !data?.items?.length ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : null}

      {typeof onChangePage === "function" ? (
        <Pagination
          style={{ margin: 16 }}
          current={data?.meta?.currentPage ?? 1}
          total={data?.meta?.totalItems ?? 0}
          pageSize={data?.meta?.itemsPerPage ?? 0}
          onChange={(page) => {
            onChangePage(page);
          }}
        />
      ) : null}
    </Styles>
  );
}

export default Table;
